import { DependencyList, useCallback, useEffect, useState } from 'react'

type UseAsyncCallback<T, U> = (args?: U) => Promise<T>

type UseAsyncProps<T, U> = {
  callback: UseAsyncCallback<T, U>
  dependencies: DependencyList
  auto?: boolean
  initialArgs?: U
  preserveValueWhileLoading?: boolean
}

type UseAsyncReturn<T, U> = {
  loading: boolean
  error: Error | undefined
  value: T | undefined
  execute: (args?: U) => void
}

export default function useAsync<T, U = undefined>({
  callback,
  dependencies = [],
  auto = true,
  initialArgs,
  preserveValueWhileLoading = false,
}: UseAsyncProps<T, U>): UseAsyncReturn<T, U> {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const [value, setValue] = useState<T | undefined>()

  const execute = useCallback((args?: U) => {
    setLoading(true)
    setError(undefined)
    if (!preserveValueWhileLoading) setValue(undefined)
    callback(args)
      .then(setValue)
      .catch(setError)
      .finally(() => setLoading(false))
  }, dependencies)

  useEffect(() => {
    if (auto) execute(initialArgs)
  }, [auto, execute, initialArgs])

  return { loading, error, value, execute }
}
